import { CountrySchema } from '@/constructs/CountrySchema';
import { Province } from '@/constructs/provinces/Province';
import type {
  IAddress,
  IAddressWithPhoneNumber,
  IBasicAddress
} from '@/services/models/Address';
import { ZodType, z, type ZodObject, type ZodTypeAny } from 'zod';

/** A schema for {@link IBasicAddress}. */
export const BasicAddressSchema = z.object({
  zipPostalCode: z.string().min(1),
  firstName: z.string().min(1),
  lastName: z.string().min(1)
}) satisfies ZodType<IBasicAddress>;

/**
 * A schema representing a complete address.
 *
 * This should be used during operations like place order, i.e.
 * when the address information is complete. Before payment
 * authorization, the address information may be incomplete.
 */
export const CompleteAddressSchema = BasicAddressSchema.extend({
  addressLine1: z.string().min(1),
  addressLine2: z.string().optional(),
  city: z.string().min(1),
  country: CountrySchema,
  stateProvince: z.nativeEnum(Province)
}) satisfies ZodType<IAddress>;

/**
 * A schema representing an incomplete address, where the only validated fields are
 * city, country, postal code, province.
 *
 * Payment providers like PayPal and Apple Pay initially return minimal address information
 * during the checkout flow (before order approval). Personal information fields (name)
 * and street details are often omitted at first and only provided after final order approval.
 *
 * This schema validates these partial addresses while maintaining IAddress type compatibility,
 * allowing the checkout process to continue with location-based operations (tax calculation,
 * shipping options) before full address details are available.
 */
export const IncompleteAddressSchema = z.object({
  addressLine1: z.string().default(''),
  addressLine2: z.string().optional(),
  city: z.string().min(1),
  country: CountrySchema,
  zipPostalCode: z.string().min(1),
  stateProvince: z.nativeEnum(Province),
  firstName: z.string().default(''),
  lastName: z.string().default('')
}) as ZodType<IAddress>;

/**
 * A schema representing a masked, complete address.
 *
 * This should be used when validating potentially masked address information,
 * such as when retrieving order history for guest users.
 */
export const MaskedCompleteAddressSchema = BasicAddressSchema.extend({
  addressLine1: z.string().min(1),
  addressLine2: z.string().optional(),
  city: z.string().min(1),
  country: CountrySchema,
  stateProvince: z.string().min(1)
}) as ZodObject<never, 'strip', ZodTypeAny, IAddress, IAddress>;

/**
 * Alias for {@link CompleteAddressSchema}.
 *
 * This will be deprecated in the future in favor of more specific schemas,
 * e.g. {@link CompleteAddressSchema} or {@link IncompleteAddressSchema}.
 *
 * A schema representing an Address.
 */
export const AddressSchema = CompleteAddressSchema;

/**
 * A schema for an address with the minimum validations to
 * be compatible with AWS.
 *
 * It is similar to {@link AddressSchema}, but it does not enforce
 * that `stateProvince` is a valid {@link Province} value.
 */
export const AWSCompatibleAddressSchema = AddressSchema.extend({
  /**
   * Some countries (like Germany) don't use states in addressing.
   * In those cases, `stateProvince` should be an empty string.
   *
   * TODO: However, AWS currently requires a state for all addresses.
   * This will be fixed with {@link https://deckersbrand.atlassian.net/browse/HP-4024 HP-4024}, but for now, to remain aligned
   * with AWS, we'll keep this as a non-empty field.
   */
  stateProvince: z.string().min(1)
}) as ZodType<IAddress>;

/** A schema representing an Address with a phone number. */
export const AddressWithPhoneNumberSchema = AddressSchema.extend({
  phoneNumber: z.string().min(1)
}) satisfies ZodType<IAddressWithPhoneNumber>;

export const MaskedCompleteAddressWithPhoneNumberSchema =
  MaskedCompleteAddressSchema.extend({
    phoneNumber: z.string().min(1)
  }) satisfies ZodType<IAddressWithPhoneNumber>;

export const BillingAddressSchema = AWSCompatibleAddressSchema;
export const MaskedBillingAddressSchema = MaskedCompleteAddressSchema;
export const MaskedBillingAddressWithPhoneNumberSchema =
  MaskedCompleteAddressWithPhoneNumberSchema;
